import { useState, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { cn } from '../../lib/utils';
import { Shield, AlertTriangle, Copy, Clock, CheckCircle2, Lock } from 'lucide-react';
import { toast } from 'sonner';

const CLIPBOARD_CLEAR_SECONDS = 60;

const redactValue = (text) => {
  return text
    .replace(/\b\d{3}-\d{2}-\d{4}\b/g, '***-**-****')          // SSN
    .replace(/\b(MRN|mrn)[-:]?\s*\S+/gi, (m) => m.slice(0, 4) + '****')  // MRN
    .replace(/\b\d{2}\/\d{2}\/\d{4}\b/g, '**/**/****')           // DOB mm/dd/yyyy
    .replace(/\b\d{4}-\d{2}-\d{2}\b/g, '****-**-**')             // DOB yyyy-mm-dd
    .replace(/\b[A-Z]{3}-\d{6,}\b/g, (m) => m.slice(0, 4) + '****')  // Member IDs
    .replace(/\(\d{3}\)\s?\d{3}-\d{4}/g, '(***) ***-****')       // Phone
    .replace(/\b[\w.-]+@[\w.-]+\.\w{2,}\b/g, '****@****.***');   // Email
};

export const useHIPAACopy = () => {
  const [showDialog, setShowDialog] = useState(false);
  const [pendingCopy, setPendingCopy] = useState(null);
  const [redact, setRedact] = useState(false);
  const [autoClear, setAutoClear] = useState(true);
  const clearTimerRef = useRef(null);

  const requestCopy = useCallback((text, label = 'PHI Data') => {
    setPendingCopy({ text, label });
    setShowDialog(true);
  }, []);

  const executeCopy = useCallback(() => {
    if (!pendingCopy) return;
    const textToCopy = redact ? redactValue(pendingCopy.text) : pendingCopy.text;
    navigator.clipboard?.writeText(textToCopy);

    if (autoClear) {
      if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
      clearTimerRef.current = setTimeout(() => {
        navigator.clipboard?.writeText('');
        toast.info('Clipboard auto-cleared', { description: 'PHI data removed from clipboard for HIPAA compliance' });
      }, CLIPBOARD_CLEAR_SECONDS * 1000);
    }

    toast.success(`${pendingCopy.label} copied`, {
      description: `PHI access logged for compliance${autoClear ? ` | Auto-clear in ${CLIPBOARD_CLEAR_SECONDS}s` : ''}${redact ? ' | Identifiers redacted' : ''}`,
      icon: <Lock className="h-4 w-4 text-emerald-600" />,
    });

    setShowDialog(false);
    setPendingCopy(null);
  }, [pendingCopy, redact, autoClear]);

  const cancelCopy = useCallback(() => {
    setShowDialog(false);
    setPendingCopy(null);
  }, []);

  const HIPAADialog = () => (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogContent className="max-w-md" data-testid="hipaa-copy-dialog">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-amber-100 flex items-center justify-center">
              <Shield className="h-4 w-4 text-amber-700" />
            </div>
            <div>
              <DialogTitle className="text-base">HIPAA-Protected Copy</DialogTitle>
              <DialogDescription className="text-xs">This action involves Protected Health Information</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          <div className="p-3 rounded-lg border border-amber-200 bg-amber-50/70 text-xs text-amber-800 flex items-start gap-2" data-testid="hipaa-warning">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">PHI Compliance Notice</p>
              <p className="mt-1 leading-relaxed">You are about to copy Protected Health Information (PHI) to your clipboard. This action will be logged per HIPAA audit requirements. Ensure you are authorized and the data is handled securely.</p>
            </div>
          </div>

          {pendingCopy && (
            <div className="p-3 rounded-lg bg-slate-50 border text-xs">
              <p className="font-medium text-slate-600 mb-1">Copying: {pendingCopy.label}</p>
              <p className="text-muted-foreground line-clamp-2 font-mono">{pendingCopy.text.slice(0, 120)}...</p>
            </div>
          )}

          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 rounded-lg border">
              <div className="flex items-center gap-2">
                <Lock className="h-3.5 w-3.5 text-slate-500" />
                <div>
                  <p className="text-xs font-medium">Redact Identifiers</p>
                  <p className="text-[10px] text-muted-foreground">Mask SSN, DOB, MRN, phone, email</p>
                </div>
              </div>
              <Switch checked={redact} onCheckedChange={setRedact} data-testid="hipaa-redact-toggle" />
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg border">
              <div className="flex items-center gap-2">
                <Clock className="h-3.5 w-3.5 text-slate-500" />
                <div>
                  <p className="text-xs font-medium">Auto-Clear Clipboard</p>
                  <p className="text-[10px] text-muted-foreground">Clear after {CLIPBOARD_CLEAR_SECONDS} seconds</p>
                </div>
              </div>
              <Switch checked={autoClear} onCheckedChange={setAutoClear} data-testid="hipaa-autoclear-toggle" />
            </div>
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={cancelCopy} data-testid="hipaa-cancel-btn">Cancel</Button>
          <Button className="bg-slate-900 hover:bg-slate-800 gap-2" onClick={executeCopy} data-testid="hipaa-confirm-copy-btn">
            <Copy className="h-3.5 w-3.5" /> I Acknowledge — Copy Data
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return { requestCopy, HIPAADialog };
};
